/**
 * Counts a number up from zero when it scrolls into view.
 * The server renders the final text, which stays available to screen readers.
 * With reduced motion the final text simply stays as it is.
 */
class AvalonCountUp extends HTMLElement {
  #observer = null;
  #frame = 0;
  #display = null;
  #target = 0;
  #grouped = false;
  #started = false;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const value = this.dataset.value || '';
    const digits = value.replace(/\D/g, '');
    if (!digits) return;

    this.#target = Number(digits);
    this.#grouped = value.includes(',');

    const final = this.querySelector('.avalon-stat__final');
    if (!final) return;

    // Keep the final width so the layout does not move while counting
    this.style.minWidth = `${Math.ceil(final.getBoundingClientRect().width)}px`;
    final.classList.add('visually-hidden');

    this.#display = document.createElement('span');
    this.#display.className = 'avalon-stat__count';
    this.#display.setAttribute('aria-hidden', 'true');
    this.#render(0);
    this.append(this.#display);

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.4 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #render(number) {
    const text = this.#grouped ? number.toLocaleString('en-US') : String(number);
    this.#display.textContent = `${this.dataset.prefix || ''}${text}${this.dataset.suffix || ''}`;
  }

  #onIntersect = ([entry]) => {
    if (!entry.isIntersecting || this.#started) return;

    this.#started = true;
    this.#observer?.disconnect();

    const duration = Number(this.dataset.duration) || 2200;
    const delay = Number(this.dataset.delay) || 0;
    const start = performance.now() + delay;

    const tick = (now) => {
      const progress = Math.min(1, Math.max(0, (now - start) / duration));
      const eased = 1 - Math.pow(1 - progress, 3);

      this.#render(Math.round(this.#target * eased));

      if (progress < 1) this.#frame = requestAnimationFrame(tick);
    };

    this.#frame = requestAnimationFrame(tick);
  };
}

if (!customElements.get('avalon-count-up')) {
  customElements.define('avalon-count-up', AvalonCountUp);
}
